import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { 
  Search, SlidersHorizontal, X, Navigation, MapPin, Star, Clock, 
  Percent, Award, Loader2
} from "lucide-react";
import { toast } from "sonner";
import { geocodeAddress } from "@/lib/api";

export default function DirectoryFilters({
  categories = [],
  searchQuery,
  setSearchQuery,
  selectedCategory,
  setSelectedCategory,
  sortBy,
  setSortBy,
  showAdvancedFilters,
  setShowAdvancedFilters,
  userLocation,
  setUserLocation,
  maxDistance,
  setMaxDistance,
  locationQuery,
  setLocationQuery,
  minRating,
  setMinRating,
  openNow,
  setOpenNow,
  hasDiscounts,
  setHasDiscounts,
  featuredOnly,
  setFeaturedOnly,
  hasReviews,
  setHasReviews,
  onSearch,
  onClearFilters
}) {
  const [locationLoading, setLocationLoading] = useState(false);
  const [geocodeLoading, setGeocodeLoading] = useState(false);

  const getUserLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser");
      return;
    }
    
    setLocationLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          source: 'gps'
        });
        setLocationQuery("Current Location");
        setLocationLoading(false);
        toast.success("Using your current location");
      },
      (error) => {
        setLocationLoading(false);
        toast.error("Unable to get your location. Try entering a zip code or city.");
      }
    );
  };

  const handleGeocodeLocation = async () => {
    if (!locationQuery || locationQuery === "Current Location") return;
    
    setGeocodeLoading(true);
    try {
      const res = await geocodeAddress(locationQuery);
      if (res.data && res.data.lat && res.data.lng) {
        setUserLocation({
          lat: res.data.lat,
          lng: res.data.lng,
          source: 'geocode',
          display_name: res.data.display_name || locationQuery
        });
        toast.success(`Found: ${res.data.display_name || locationQuery}`);
      } else {
        toast.error("Could not find that location. Try a different address or zip code.");
      }
    } catch (err) {
      toast.error("Failed to search location. Try again.");
    }
    setGeocodeLoading(false);
  };

  const clearLocation = () => {
    setUserLocation(null);
    setLocationQuery("");
    setMaxDistance("");
  };

  const hasActiveFilters = selectedCategory || minRating || hasReviews || searchQuery || openNow || hasDiscounts || featuredOnly || maxDistance || userLocation;

  return (
    <form onSubmit={onSearch} className="space-y-3">
      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#6B665E]" />
          <Input 
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search businesses..."
            className="pl-10 border-[#E5E0D5] font-['Nunito'] text-sm"
            data-testid="directory-search-input"
          />
        </div>
        
        <Select value={selectedCategory || "all"} onValueChange={(v) => setSelectedCategory(v === "all" ? "" : v)}>
          <SelectTrigger className="w-full md:w-48 border-[#E5E0D5] font-['Nunito'] text-sm">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all" className="font-['Nunito']">All Categories</SelectItem>
            {categories.map(cat => (
              <SelectItem key={cat} value={cat} className="font-['Nunito'] text-sm">{cat}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-full md:w-44 border-[#E5E0D5] font-['Nunito'] text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="rating" className="font-['Nunito']">Top Rated</SelectItem>
            <SelectItem value="newest" className="font-['Nunito']">Newest</SelectItem>
            <SelectItem value="most_reviewed" className="font-['Nunito']">Most Reviewed</SelectItem>
            <SelectItem value="most_recommended" className="font-['Nunito']">Most Recommended</SelectItem>
            {userLocation && <SelectItem value="distance" className="font-['Nunito']">Nearest First</SelectItem>}
            <SelectItem value="name_asc" className="font-['Nunito']">Name A-Z</SelectItem>
            <SelectItem value="name_desc" className="font-['Nunito']">Name Z-A</SelectItem>
          </SelectContent>
        </Select>
        
        <Button 
          type="button" 
          variant="outline"
          onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
          className={`border-[#E5E0D5] font-['Nunito'] ${showAdvancedFilters ? 'bg-[var(--liturgical-primary)]/10 border-[var(--liturgical-primary)]' : ''}`}
          data-testid="advanced-filters-btn"
        >
          <SlidersHorizontal className="w-4 h-4 mr-2" />
          Filters
          {hasActiveFilters && (
            <Badge className="ml-2 bg-[var(--liturgical-primary)] text-white text-[10px] px-1.5">
              {[selectedCategory, minRating, hasReviews, searchQuery, openNow, hasDiscounts, featuredOnly, maxDistance, userLocation].filter(Boolean).length}
            </Badge>
          )}
        </Button>
        
        <Button type="submit" className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']">
          Search
        </Button>
      </div>

      {/* Advanced Filters */}
      {showAdvancedFilters && (
        <div className="pt-3 border-t border-[#E5E0D5]">
          <div className="flex flex-wrap items-center gap-3">
            {/* Location Search with Geocoding */}
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-1">
                <MapPin className="w-4 h-4 text-[#6B665E]" />
                <Input 
                  value={locationQuery}
                  onChange={e => setLocationQuery(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleGeocodeLocation())}
                  placeholder="Zip code or city..."
                  className="w-44 border-[#E5E0D5] font-['Nunito'] text-sm h-9"
                  data-testid="location-search-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleGeocodeLocation}
                  disabled={geocodeLoading || !locationQuery || locationQuery === "Current Location"}
                  className="font-['Nunito'] text-xs h-9 border-[#E5E0D5]"
                  data-testid="geocode-btn"
                >
                  {geocodeLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : "Find"}
                </Button>
              </div>
              
              <span className="text-xs text-[#6B665E] font-['Nunito']">or</span>
              
              <Button
                type="button"
                variant={userLocation?.source === 'gps' ? "default" : "outline"}
                size="sm"
                onClick={getUserLocation}
                disabled={locationLoading}
                className={`font-['Nunito'] text-xs h-9 ${userLocation?.source === 'gps' ? 'bg-[var(--liturgical-primary)]' : 'border-[#E5E0D5]'}`}
                data-testid="use-location-btn"
              >
                {locationLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Navigation className="w-3 h-3 mr-1" />}
                Use My Location
              </Button>
              
              {userLocation && (
                <>
                  <Select value={maxDistance || "25"} onValueChange={setMaxDistance}>
                    <SelectTrigger className="w-32 border-[#E5E0D5] font-['Nunito'] text-sm h-9">
                      <SelectValue placeholder="Distance" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="5" className="font-['Nunito']">Within 5 mi</SelectItem>
                      <SelectItem value="10" className="font-['Nunito']">Within 10 mi</SelectItem>
                      <SelectItem value="25" className="font-['Nunito']">Within 25 mi</SelectItem>
                      <SelectItem value="50" className="font-['Nunito']">Within 50 mi</SelectItem>
                      <SelectItem value="100" className="font-['Nunito']">Within 100 mi</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={clearLocation}
                    className="font-['Nunito'] text-xs h-9 text-red-600 hover:text-red-700"
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </>
              )}
            </div>
            
            {/* Location indicator */}
            {userLocation && (
              <Badge className="bg-green-100 text-green-700 font-['Nunito'] text-xs">
                <MapPin className="w-3 h-3 mr-1" />
                {userLocation.source === 'gps' ? 'Using GPS' : (userLocation.display_name || locationQuery)}
              </Badge>
            )}
            
            {/* Minimum Rating Filter */}
            <div className="flex items-center gap-2">
              <Star className="w-4 h-4 text-[var(--liturgical-secondary)]" />
              <Select value={minRating || "any"} onValueChange={(v) => setMinRating(v === "any" ? "" : v)}>
                <SelectTrigger className="w-36 border-[#E5E0D5] font-['Nunito'] text-sm h-9">
                  <SelectValue placeholder="Min Rating" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="any" className="font-['Nunito']">Any Rating</SelectItem>
                  <SelectItem value="4" className="font-['Nunito']">4+ Stars</SelectItem>
                  <SelectItem value="3" className="font-['Nunito']">3+ Stars</SelectItem>
                  <SelectItem value="2" className="font-['Nunito']">2+ Stars</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Toggle Filters */}
            <Button
              type="button"
              variant={openNow ? "default" : "outline"}
              size="sm"
              onClick={() => setOpenNow(!openNow)}
              className={`font-['Nunito'] text-xs h-9 ${openNow ? 'bg-green-600' : 'border-[#E5E0D5]'}`}
              data-testid="open-now-filter"
            >
              <Clock className="w-3 h-3 mr-1" />
              Open Now
            </Button>
            
            <Button
              type="button"
              variant={hasDiscounts ? "default" : "outline"}
              size="sm"
              onClick={() => setHasDiscounts(!hasDiscounts)}
              className={`font-['Nunito'] text-xs h-9 ${hasDiscounts ? 'bg-[var(--liturgical-primary)]' : 'border-[#E5E0D5]'}`}
              data-testid="has-discounts-filter"
            >
              <Percent className="w-3 h-3 mr-1" />
              Parishioner Discounts
            </Button>
            
            <Button
              type="button"
              variant={featuredOnly ? "default" : "outline"}
              size="sm"
              onClick={() => setFeaturedOnly(!featuredOnly)}
              className={`font-['Nunito'] text-xs h-9 ${featuredOnly ? 'bg-amber-500' : 'border-[#E5E0D5]'}`}
              data-testid="featured-filter"
            >
              <Award className="w-3 h-3 mr-1" />
              Featured
            </Button>
            
            <Button
              type="button"
              variant={hasReviews ? "default" : "outline"}
              size="sm"
              onClick={() => setHasReviews(!hasReviews)}
              className={`font-['Nunito'] text-xs h-9 ${hasReviews ? 'bg-[var(--liturgical-primary)]' : 'border-[#E5E0D5]'}`}
              data-testid="has-reviews-filter"
            >
              Has Reviews
            </Button>
            
            {/* Clear Filters */}
            {hasActiveFilters && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={onClearFilters}
                className="font-['Nunito'] text-xs text-[var(--liturgical-primary)] h-9"
              >
                <X className="w-3 h-3 mr-1" />
                Clear All
              </Button>
            )}
          </div>
        </div>
      )}
    </form>
  );
}
